import { jsx as _jsx } from "react/jsx-runtime";
import { Box, Button, HStack, Stack, Text } from '@chakra-ui/react';
import { FaArrowDown } from '@react-icons/all-files/fa/FaArrowDown';
import { FaArrowUp } from '@react-icons/all-files/fa/FaArrowUp';
import { FaTrash } from '@react-icons/all-files/fa/FaTrash';
import { FiBox } from '@react-icons/all-files/fi/FiBox';
import { TuneSelectorButton } from '../../components/TuneSelectorButton';
import { JaenSectionBlockProvider } from '../../contexts/block';
import { useNotificationsContext } from '../../contexts/notifications';
import { useContentManagement } from '../../hooks/use-content-management';
import { useSectionField } from '../../hooks/use-section-field';
import { withRedux } from '../../redux';
import { HighlightTooltip } from '../components/HighlightTooltip';
import { SectionBlockSelectorButton } from './components/SectionBlockSelectorButton';
export const SectionField = withRedux(({ name, label, blocks: unfilteredBlocks, ...rest }) => {
    const { confirm, toast } = useNotificationsContext();
    const blocks = unfilteredBlocks.reduce((acc, block) => {
        const index = acc.findIndex(el => el.options.name === block.options.name);
        if (index === -1) {
            return [...acc, block];
        }
        return [...acc.slice(0, index), block, ...acc.slice(index + 1)];
    }, []);
    const { onSectionAdd, onSectionDelete, onSectionMove, onSectionAppend, onSectionPrepend, section, sectionsDict, sectionPath } = useSectionField(name, blocks);
    const { isEditing } = useContentManagement();
    const Wrapper = rest.as || Stack;
    let tooltipButtons = label
        ? [
            _jsx(Button, { variant: 'field-highlighter-tooltip-text', size: "xs", children: _jsx(Text, { as: "span", lineClamp: 1, children: label }) }, "section-field-tooltip-button-add")
        ]
        : [];
    const blocksForSelector = blocks.map(({ options }) => ({
        slug: options.name,
        label: options.label,
        Icon: options.Icon || FiBox
    }));
    const handleSectionAdd = (block, type, item) => {
        if (item) {
            if (type === 'prepend') {
                onSectionPrepend(block.slug, item.id, item.ptrPrev);
            }
            else if (type === 'append') {
                onSectionAppend(block.slug, item.id, item.ptrNext);
            }
            else {
                onSectionAdd(block.slug, [item.id, item.ptrNext]);
            }
        }
        else {
            if (type === 'prepend' && section.ptrHead) {
                onSectionPrepend(block.slug, section.ptrHead, null);
            }
            else if (type === 'append' && section.ptrTail) {
                onSectionAppend(block.slug, section.ptrTail, null);
            }
            else {
                onSectionAdd(block.slug, [null, null]);
            }
        }
        toast({
            title: 'Block added',
            description: `Block has been added to the ${label} section`,
            status: 'info'
        });
    };
    const handleSectionDelete = async (id, ptrPrev, ptrNext) => {
        const shouldDelete = await confirm({
            title: 'Delete block',
            message: `Are you sure you want to remove this block from the ${label} section?`
        });
        if (shouldDelete) {
            onSectionDelete(id, ptrPrev, ptrNext);
            toast({
                title: 'Block deleted',
                description: `Block has been removed from the ${label} section`,
                status: 'info'
            });
        }
    };
    if (blocks.length > 0) {
        tooltipButtons = tooltipButtons.concat([
            _jsx(HStack, { gap: "0.5", children: _jsx(SectionBlockSelectorButton, { onBlockAdd: handleSectionAdd, blocks: blocksForSelector, onlyAdd: section.items.length === 0 }) }, "section-field-tooltip-buttons")
        ]);
    }
    return (_jsx(HighlightTooltip, { id: name, isEditing: isEditing, actions: tooltipButtons, as: Wrapper, minH: "64", p: "4", className: rest.className, style: rest.style, ...rest.props, children: section.items.map((item, index) => {
            const s = sectionsDict[item.type];
            if (s == null) {
                console.error(`Section type ${item.type} is not found in sections dictionary!`);
                return null;
            }
            const SectionWrapper = rest.sectionAs || Box;
            const sectionProps = typeof rest.sectionProps === 'function'
                ? rest.sectionProps({
                    count: index + 1,
                    totalSections: section.items.length,
                    section: item
                })
                : rest.sectionProps;
            return (_jsx(HighlightTooltip, { id: `${name}-${index}`, as: SectionWrapper, isEditing: isEditing, actions: [
                    _jsx(Button, { variant: "field-highlighter-tooltip-text", children: _jsx(Text, { as: "span", lineClamp: 1, children: s.Component.options.label }) }, `section-field-tooltip-button-${item.id}`),
                    _jsx(SectionBlockSelectorButton, { onBlockAdd: (block, type) => {
                            handleSectionAdd(block, type, item);
                        }, blocks: blocksForSelector, onlyAdd: false }, `section-field-tooltip-block-selector-${item.id}`),
                    _jsx(TuneSelectorButton, { "aria-label": "Section tune selector", activeTunes: item.id === section.ptrHead
                            ? [{ name: 'move-up' }]
                            : item.id === section.ptrTail
                                ? [{ name: 'move-down' }]
                                : [], tunes: [
                            {
                                type: 'tune',
                                name: 'move-up',
                                label: 'Move up',
                                Icon: FaArrowUp,
                                onTune: () => {
                                    const prevItem = section.items.find(el => el.id === item.ptrPrev)
                                        ?.ptrPrev || null;
                                    onSectionMove(item.id, item.ptrPrev, item.ptrNext, prevItem, 'up');
                                },
                                isHiddenOnActive: true
                            },
                            {
                                type: 'tune',
                                name: 'move-down',
                                label: 'Move down',
                                Icon: FaArrowDown,
                                onTune: () => {
                                    const nextItem = section.items.find(el => el.id === item.ptrNext)
                                        ?.ptrNext || null;
                                    onSectionMove(item.id, item.ptrPrev, item.ptrNext, nextItem, 'down');
                                },
                                isHiddenOnActive: true
                            },
                            {
                                type: 'tune',
                                name: 'delete',
                                label: 'Delete',
                                Icon: FaTrash,
                                onTune: () => {
                                    void handleSectionDelete(item.id, item.ptrPrev, item.ptrNext);
                                }
                            }
                        ] }, `section-field-tooltip-tune-selector-${item.id}`)
                ], p: "2", ...sectionProps, children: _jsx(JaenSectionBlockProvider, { path: sectionPath, id: item.id, position: index, Component: s.Component }) }, item.id));
        }) }));
});
